import { PressureCode, TemperatureCode } from '@icommerce/units';
import { round10, UnitSystem } from '@igus/kopla-domain';
import type { IbhMaterialResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { imperialCountries } from '@hub/bearing-hub/common/constants';
import type { IbhFilterValues } from '@hub/hooks/states/useIbhFilterValuesStore';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { IbhFilterKeys } from '@hub/libs/constants';

type MaterialBooleanKey = keyof Pick<
  IbhMaterialResponseDto,
  | 'foodContactFDACompliant'
  | 'foodContactEUCompliant'
  | 'dirtResistant'
  | 'lowWaterAbsorption'
  | 'highChemicalResistance'
  | 'underWaterCompliant'
  | 'electricallyConductive'
  | 'heavyDuty'
  | 'PFASFree'
  | 'PTFEFree'
>;

const distanceFactor = 1 / 25.4;
const forceFactor = 0.2248089439;
const areaFactor = distanceFactor * distanceFactor;
const pressureFactor = forceFactor / areaFactor;

const materialBooleanFilters = [
  { filterKey: IbhFilterKeys.FDA_COMPLIANCE, materialKey: 'foodContactFDACompliant' },
  { filterKey: IbhFilterKeys.EU102011_COMPLIANCE, materialKey: 'foodContactEUCompliant' },
  { filterKey: IbhFilterKeys.DIRT_AND_DUST, materialKey: 'dirtResistant' },
  { filterKey: IbhFilterKeys.LOW_WATER_ABSORBTION, materialKey: 'lowWaterAbsorption' },
  { filterKey: IbhFilterKeys.CHEMICALS, materialKey: 'highChemicalResistance' },
  { filterKey: IbhFilterKeys.UNDERWATER_SPLASHWATER, materialKey: 'underWaterCompliant' },
  { filterKey: IbhFilterKeys.ELECTRICALLY_CONDUCTIVE, materialKey: 'electricallyConductive' },
  { filterKey: IbhFilterKeys.HEAVY_DUTY, materialKey: 'heavyDuty' },
  { filterKey: IbhFilterKeys.PFAS_FREE, materialKey: 'PFASFree' },
  { filterKey: IbhFilterKeys.PTFE_FREE, materialKey: 'PTFEFree' },
] as const satisfies readonly { filterKey: IbhFilterKeys; materialKey: MaterialBooleanKey }[];

const convertTemperatureUnit = (
  { value, unit }: { value: number; unit?: TemperatureCode },
  toUnit: TemperatureCode
): number => {
  if (unit === toUnit) {
    return value;
  }
  if (toUnit === TemperatureCode.FAHRENHEIT) {
    return round10((value * 9) / 5 + 32);
  }

  return round10((5 / 9) * (value - 32));
};

const convertPressureUnit = ({ value, unit }: { value: number; unit?: PressureCode }, toUnit: PressureCode) => {
  if (unit === toUnit) {
    return value;
  }
  if (toUnit === PressureCode.MEGAPASCAL) {
    return round10(value / pressureFactor);
  }

  return round10(value * pressureFactor);
};

export const getQueryableMaterials = (
  materials: IbhMaterialResponseDto[],
  filterValues: IbhFilterValues,
  session: Session,
  unitSystem: UnitSystem = UnitSystem.metric
): IbhMaterialResponseDto[] => {
  const temperatureUnit = unitSystem === UnitSystem.metric ? TemperatureCode.CELSIUS : TemperatureCode.FAHRENHEIT;
  const pressureUnit =
    unitSystem === UnitSystem.metric && !imperialCountries.includes(session?.country)
      ? PressureCode.MEGAPASCAL
      : PressureCode.PSI;

  const getNumberFilterValue = (filterKey: IbhFilterKeys): number | undefined => {
    const filterValue = filterValues[filterKey];

    return filterValue === undefined ? undefined : (filterValue as number);
  };

  const temperatureRangeMinValue = getNumberFilterValue(IbhFilterKeys.TEMPERATURE_RANGE_MIN);
  const temperatureRangeMaxValue = getNumberFilterValue(IbhFilterKeys.TEMPERATURE_RANGE_MAX);
  const maxSurfacePressureValue = getNumberFilterValue(IbhFilterKeys.MAX_SURFACE_PRESSURE);

  const temperatureRangeMin =
    temperatureRangeMinValue === undefined
      ? undefined
      : convertTemperatureUnit(
          {
            value: temperatureRangeMinValue,
            unit: temperatureUnit,
          },
          TemperatureCode.CELSIUS
        );

  const temperatureRangeMax =
    temperatureRangeMaxValue === undefined
      ? undefined
      : convertTemperatureUnit(
          {
            value: temperatureRangeMaxValue,
            unit: temperatureUnit,
          },
          TemperatureCode.CELSIUS
        );

  const maxSurfacePressure =
    maxSurfacePressureValue === undefined
      ? undefined
      : convertPressureUnit(
          {
            value: maxSurfacePressureValue,
            unit: pressureUnit,
          },
          PressureCode.MEGAPASCAL
        );

  const matchesBooleanFilter = (filterKey: IbhFilterKeys, materialValue: boolean | undefined): boolean => {
    const filterValue = filterValues[filterKey] as boolean | undefined;

    return filterValue === undefined || (filterValue === true && materialValue === true);
  };

  return materials.filter(material => {
    const matchesBooleanFilters = materialBooleanFilters.every(({ filterKey, materialKey }) =>
      matchesBooleanFilter(filterKey, material[materialKey])
    );
    const matchesMinTemperature =
      temperatureRangeMin === undefined ||
      (temperatureRangeMin >= material.minTemperature && temperatureRangeMin <= material.maxTemperature);
    const matchesMaxTemperature =
      temperatureRangeMax === undefined ||
      (temperatureRangeMax >= material.minTemperature && temperatureRangeMax <= material.maxTemperature);
    const matchesSurfacePressure =
      maxSurfacePressure === undefined || maxSurfacePressure <= material.maxSurfacePressure;

    return matchesBooleanFilters && matchesMinTemperature && matchesMaxTemperature && matchesSurfacePressure;
  });
};
